/**
 * The Pay What You Want currency row, as an offering form's Yup schema.
 * Pure TypeScript - no React imports (Yup is permitted in every layer).
 *
 * All four offering types drop this into their own `currencies` array, so the two
 * validation rules reach the forms from one place rather than four.
 *
 * Requiredness lives here because it is a form concern. The two rules themselves live in
 * `validatePwywRow`, which returns at most one error per row — that is what keeps a row
 * failing both rules from showing two messages.
 */

import * as yup from "yup";
import {toPricingCurrencyRow} from "../data/mappers/offering-form-pricing.mapper";
import {validatePwywRow} from "./pwyw-row-validation";

export const MINIMUM_PRICE_REQUIRED = "Minimum price is required";
export const SUGGESTED_PRICE_REQUIRED = "Suggested price is required";
export const CURRENCY_REQUIRED = "Currency is required";
const NOT_A_NUMBER = "Must be a number";

/**
 * An input the coach has emptied reads as `""`, which Yup would otherwise cast to `NaN`
 * and report as "Must be a number". It is not a bad number — it is no number, which is
 * what `required` is for.
 */
function emptyMeansUnset(value: number, original: unknown): number | undefined {
  return original === "" || original === null ? undefined : value;
}

/**
 * Surfaces the row rule that belongs against one field, and stays quiet about the other.
 *
 * `validatePwywRow` decides which of the two rules to report; this only asks whether the
 * one it chose belongs here. That is how Rule A suppresses Rule B on the same row.
 */
function reportsRowErrorAgainst(field: "minimum" | "suggested") {
  return function (this: yup.TestContext) {
    const error = validatePwywRow(toPricingCurrencyRow(this.parent));

    return error?.field === field ? this.createError({message: error.message}) : true;
  };
}

/**
 * One currency row under Pay What You Want: a currency, a Minimum Price and a Suggested
 * Price, all three required.
 */
export const pwywCurrencyRowSchema = yup.object().shape({
  callPriceCurrency: yup.string().trim().required(CURRENCY_REQUIRED),
  minCallPrice: yup
    .number()
    .transform(emptyMeansUnset)
    .typeError(NOT_A_NUMBER)
    .required(MINIMUM_PRICE_REQUIRED)
    .test("pwyw-minimum-price", "", reportsRowErrorAgainst("minimum")),
  callPrice: yup
    .number()
    .transform(emptyMeansUnset)
    .typeError(NOT_A_NUMBER)
    .required(SUGGESTED_PRICE_REQUIRED)
    .test("pwyw-suggested-price", "", reportsRowErrorAgainst("suggested")),
});
